import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "src/contexts/AppContext";
import { trackEvent } from "src/hooks/useInstrumentation";
import useErrorHandler from "src/hooks/utility/useErrorHandler";
import { useSnackbarContext } from "src/SnackbarProvider";

type Props = {
  /**
   * Optional prompts open over the requested page and may be dismissed for the
   * current inactivity event. The default keeps the legacy gated-page launcher
   * used by non-inactivity account states.
   */
  presentation?: "page" | "optional";
  dismissalKey?: string;
};

const DISMISSED_VALUE = "dismissed";

function wasDismissed(key?: string) {
  if (!key) return false;

  try {
    return sessionStorage.getItem(key) === DISMISSED_VALUE;
  } catch {
    return false;
  }
}

function rememberDismissal(key?: string) {
  if (!key) return;

  try {
    sessionStorage.setItem(key, DISMISSED_VALUE);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers. The local
    // open state still dismisses the prompt for the current page.
  }
}

function forgetDismissal(key?: string) {
  if (!key) return;

  try {
    sessionStorage.removeItem(key);
  } catch {
    // See rememberDismissal. There is nothing else to roll back here.
  }
}

export default function ReactivationDialog({
  presentation = "page",
  dismissalKey,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isOptional = presentation === "optional";
  const [reason, setReason] = useState("");
  const [commitment, setCommitment] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(
    () => isOptional && !wasDismissed(dismissalKey)
  );
  const [checked, setChecked] = useState(false);
  const { clients } = useAppContext();
  const reactivateUserAccount = httpsCallable<
    { reason: string; commitment: string },
    { restoredStatus: string | null }
  >(clients.functions, "reactivateUserAccountSecondGen");
  const errorHandler = useErrorHandler();
  const snackbarContext = useSnackbarContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (open && isOptional) {
      trackEvent("Reactivation Dialog - Viewed");
    }
  }, [isOptional, open]);

  const handleOpen = () => {
    setOpen(true);
    trackEvent("Reactivation Dialog - Click on Open");
  };

  const handleClose = () => {
    if (isOptional) rememberDismissal(dismissalKey);
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (loading) return;

    const trimmedReason = reason.trim();
    const trimmedCommitment = commitment.trim();

    // Let the student continue immediately while the callable catches up. If
    // it fails, the catch block rolls the optimistic dismissal back and keeps
    // their answers in place so they can retry.
    setLoading(true);
    if (isOptional) rememberDismissal(dismissalKey);
    setOpen(false);

    try {
      const result = await reactivateUserAccount({
        reason: trimmedReason,
        commitment: trimmedCommitment,
      });
      const { restoredStatus } = result.data;

      trackEvent("Reactivation Dialog - Submitted Reactivation", {
        reason: trimmedReason,
        commitment: trimmedCommitment,
        restoredStatus: restoredStatus ?? "null",
      });
      setReason("");
      setCommitment("");
      setChecked(false);
      snackbarContext.alert("success", t("Your account is active again."));

      if (
        restoredStatus === "conditionally_enrolled" ||
        restoredStatus === "on_hold"
      ) {
        navigate("/");
      } else {
        navigate("/onboarding/survey");
      }
    } catch (err) {
      if (isOptional) forgetDismissal(dismissalKey);
      setOpen(true);
      errorHandler(err);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    reason.trim().length >= 5 &&
    commitment.trim().length >= 5 &&
    checked &&
    !loading;

  return (
    <>
      {!isOptional && (
        <Stack spacing={3} sx={{ width: "100%", maxWidth: 600 }}>
          <Alert severity="warning">
            <AlertTitle>{t("Your account is inactive")}</AlertTitle>
            <Typography>
              {t(
                "You can ask to return when you are ready to continue your training."
              )}
            </Typography>
          </Alert>
          <Box
            sx={(currentTheme) => ({
              border: `1px solid ${currentTheme.palette.borderToken}`,
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
            })}
          >
            <Stack spacing={2}>
              <Typography variant="h3">{t("Ready to return?")}</Typography>
              <Typography>
                {t(
                  "Tell us what changed so your coach knows how to support you."
                )}
              </Typography>
              <Button
                onClick={handleOpen}
                variant="contained"
                color="primary"
                size="large"
                sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
              >
                {t("Request Reactivation")}
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        transitionDuration={
          isOptional
            ? { enter: theme.transitions.duration.enteringScreen, exit: 0 }
            : undefined
        }
        aria-labelledby="reactivation-dialog-title"
        aria-describedby="reactivation-dialog-description"
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "calc(100% - 32px)" },
            height: { xs: "100dvh", sm: "auto" },
            maxHeight: { xs: "100dvh", sm: "calc(100dvh - 32px)" },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle
          id="reactivation-dialog-title"
          sx={{
            pr: 7,
            pb: 1,
            pt: { xs: "max(16px, env(safe-area-inset-top))", sm: 3 },
          }}
        >
          {t("Ready to get back on track?")}
        </DialogTitle>
        <IconButton
          aria-label={t("Close")}
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: { xs: "max(4px, env(safe-area-inset-top))", sm: 12 },
            width: 44,
            height: 44,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
          <Stack spacing={3}>
            <Typography id="reactivation-dialog-description">
              {t(
                "Tell us what got in the way and what has changed. Your answers help your coach support you."
              )}
            </Typography>

            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("What we ask from you")}
              </Typography>
              <Stack component="ul" spacing={1} sx={{ my: 0, pl: 3 }}>
                <Typography component="li">
                  {t("Reply to the Emerge team within 48 hours.")}
                </Typography>
                <Typography component="li">
                  {t("Make progress with your coursework each day.")}
                </Typography>
                <Typography component="li">
                  {t("Accounts can be reactivated up to two times.")}
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={(event) => setChecked(event.target.checked)}
                  />
                }
                label={t("I understand and agree to these expectations")}
                sx={{
                  alignItems: "flex-start",
                  mt: 1.5,
                  mr: 0,
                  "& .MuiCheckbox-root": { mt: -0.75 },
                }}
              />
            </Box>

            <TextField
              label={t("What got in the way?")}
              placeholder={t("What made it hard to continue training?")}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 1000 }}
            />

            <TextField
              label={t("What has changed?")}
              placeholder={t("What will help you stay on track now?")}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              value={commitment}
              onChange={(event) => setCommitment(event.target.value)}
              inputProps={{ maxLength: 1000 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: 2,
            pb: {
              xs: "max(16px, env(safe-area-inset-bottom))",
              sm: 3,
            },
          }}
        >
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1}
            justifyContent="flex-end"
            sx={{ width: "100%" }}
          >
            <Button
              onClick={handleClose}
              color="inherit"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {isOptional ? t("Maybe later") : t("Cancel")}
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="primary"
              disabled={!canSubmit}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {t("Reactivate my account")}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
